'use client';

import { useFormContext } from 'react-hook-form';

import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { Modal, ModalButton, BaseModalContent } from '@/components/modal';

import type { TermsAndConditionsModalProps } from './index.types';
import type { FieldValues } from 'react-hook-form';

export const TermsAndConditionsModal = <T extends FieldValues>({
  analyticsEventName,
  children,
  heading = 'Terms and conditions',
  isModalOpen = false,
  onReadValue,
  readConditionsName,
  setIsModalOpen,
}: TermsAndConditionsModalProps<T>) => {
  const { watch, setValue } = useFormContext<T>();

  const isTermsRead = watch(readConditionsName);

  function closeModal() {
    if (analyticsEventName) {
      trackAnalyticsEvent(analyticsEventName);
    }
    setIsModalOpen(false);
  }

  function openModal() {
    setIsModalOpen(true);
  }

  return (
    <Modal
      heading={heading}
      isModalOpen={isModalOpen}
      openModal={openModal}
    >
      <BaseModalContent
        hideScrollbar={false}
        handleHasOverflow={(hasOverflow) => {
          if (!hasOverflow && !isTermsRead) {
            setValue(readConditionsName, onReadValue);
          }
        }}
        onScroll={(event) => {
          const target = event.target as HTMLElement;

          // Offset from the bottom so as not to do an exact match
          const bottom =
            target.scrollHeight - target.scrollTop - 16 <= target.clientHeight;

          if (bottom && !isTermsRead) {
            setValue(readConditionsName, onReadValue);
          }
        }}
        buttonComponent={
          <ModalButton
            variant="secondary"
            onClick={closeModal}
            disabled={!isTermsRead}
          >
            Return to form
          </ModalButton>
        }
      >
        {children}
      </BaseModalContent>
    </Modal>
  );
};
